import React from "react";
import { Badge } from "../../calendar/Badge";
import { FaSync } from "react-icons/fa";
import { Rocket } from "lucide-react";
import { useIsMobile } from "../../../hooks/useIsMobile";
import type { PropertyGroupPayload } from "../../../types/userListing";
import { propertyGroupBadgeLabel } from "../../../utils/propertyGroupCopy";
import PropertyGroupNote from "./PropertyGroupNote";

interface PropertyHeaderProps {
    formattedAddress: string;
    propertyType: string;
    status: string | undefined;
    isSyncing?: boolean;
    // MB-1472: commercial listings only - marketingCategory gates the subtype suffix,
    // mlsPropertySubType supplies its text (e.g. "Commercial Sale - Office").
    marketingCategory?: string;
    mlsPropertySubType?: string;
    // Found in QA: `propertyType` is derived api-side from StructureType /
    // ArchitecturalStyle, which are residential-only and null on every
    // commercial listing. The badge's own condition then suppressed the whole
    // thing, subtype suffix included, so a commercial listing showed no type
    // badge at all. On the commercial path the base label is the MLS property
    // type - which is what the "Commercial Sale - Office" example above always
    // described.
    mlsPropertyType?: string;
    // MB-1520: set when this record shares a property with another MLS record.
    // canEditGroup gates the Grouped | Separate control (Autopilot on);
    // autopilotEnabled (raw hook value, null = unknown) picks the note's wording.
    propertyGroup?: PropertyGroupPayload;
    listingId?: string;
    canEditGroup?: boolean;
    autopilotEnabled?: boolean | null;
    onGroupChanged?: () => Promise<void> | void;
    // MB-1546: the campaign flag Auto Pilot stamps on adoption - same field the
    // listing card's pill reads, so the two can never disagree.
    managedByAutopilot?: boolean;
}

// Trestle type tokens are space-less (e.g. "CommercialSale"). Split on capitals for a
// readable label - same formatting MlsListingReviewModal/MlsSetupReviewModal use.
const formatPropertyType = (value: string): string =>
    value
        .replace(/([A-Z])/g, " $1")
        .replace(/,/g, ", ")
        .trim();

const ListingHeader: React.FC<PropertyHeaderProps> = ({
    formattedAddress,
    propertyType,
    status,
    isSyncing = false,
    marketingCategory,
    mlsPropertySubType,
    mlsPropertyType,
    propertyGroup,
    listingId,
    canEditGroup,
    autopilotEnabled,
    onGroupChanged,
    managedByAutopilot,
}) => {
    const isMobile = useIsMobile(800);
    const baseTypeLabel =
        marketingCategory && mlsPropertyType ? mlsPropertyType : propertyType;

    return (
        <div className="flex flex-col mb-4">
            {isSyncing && (
                <div className="mb-4 py-2 px-4 rounded-lg text-[#DBA507] font-semibold border-4 border-primary bg-white text-center flex flex-col justify-center items-center">
                    <div className="flex items-center">
                        {!isMobile && (
                            <FaSync
                                className="animate-spin text-primary mr-2"
                                size={20}
                            />
                        )}
                        Syncing the latest MLS data, just a{" "}
                        {!isMobile && "few more"} moment{!isMobile && "s..."}
                    </div>
                    <span className="ml-2 text-xs text-primary italic">
                        Hang tight — editing is paused during MLS update.
                    </span>
                </div>
            )}
            {/* Commercial takes its base label from the MLS property type;
                residential keeps StructureType/ArchitecturalStyle as before. */}
            <h1 className="text-3xl font-bold text-primary">
                {formattedAddress}
            </h1>
            <div className="flex flex-wrap items-center gap-2 mt-2">
                {baseTypeLabel && (
                    <Badge variant="secondary">
                        {formatPropertyType(baseTypeLabel)}
                        {marketingCategory && mlsPropertySubType
                            ? ` - ${formatPropertyType(mlsPropertySubType)}`
                            : ""}
                    </Badge>
                )}
                {status && (
                    <Badge
                        variant={
                            status !== "Active" && status !== "ComingSoon"
                                ? "destructive"
                                : "primary"
                        }
                    >
                        {status.replace(/([A-Z])/g, " $1")}
                    </Badge>
                )}
                {propertyGroup && (
                    <Badge variant="secondary">
                        {propertyGroupBadgeLabel(propertyGroup)}
                    </Badge>
                )}
                {managedByAutopilot === true && (
                    <Badge
                        variant="primary"
                        className="gap-1.5"
                        title="Auto Pilot creates and schedules this listing's posts."
                    >
                        <Rocket className="h-3 w-3" />
                        Auto Pilot
                    </Badge>
                )}
            </div>
            {propertyGroup && listingId && (
                <PropertyGroupNote
                    propertyGroup={propertyGroup}
                    listingId={listingId}
                    canEdit={!!canEditGroup}
                    autopilotEnabled={autopilotEnabled}
                    onChanged={onGroupChanged ?? (() => undefined)}
                />
            )}
        </div>
    );
};

export default ListingHeader;
